import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { b as breakpoints } from './index5.js';
import { D as DomService, T as TrackingService } from './index3.js';
import { a as ButtonAppearance, b as ButtonType, d as defineCustomElement$a } from './roche-button2.js';
import './index2.js';
import { C as CookieService } from './index11.js';
import { T as TranslationService } from './index6.js';
import { D as DownloadService } from './index12.js';
import { d as defineCustomElement$9 } from './roche-column2.js';
import { d as defineCustomElement$8 } from './roche-columns2.js';
import { d as defineCustomElement$7 } from './roche-container2.js';
import { d as defineCustomElement$6 } from './roche-grid2.js';
import { d as defineCustomElement$5 } from './roche-headline2.js';
import { d as defineCustomElement$4 } from './roche-icon2.js';
import { d as defineCustomElement$3 } from './roche-select2.js';
import { d as defineCustomElement$2 } from './roche-tooltip2.js';

const rocheOnClickNoticeCss = ":host{display:block;margin-top:3rem;z-index:350;position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0, 0, 0, 0.5);margin:0;display:flex;align-items:center;justify-content:center;overflow-y:auto;opacity:0;pointer-events:none;padding:1rem}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 90rem){:host{margin-top:0}}:host>roche-grid{width:100%}:host .wrapper{background-color:#fff;background-clip:border-box;padding:2.5rem 1.25rem;overflow-y:auto;max-height:calc(100vh - 2.5rem * 2)}@media (min-width: 48rem){:host .wrapper{padding:4rem 2rem}}:host roche-headline{margin-top:0}:host .button-wrapper{display:block;margin-top:1.5rem}@media (min-width: 48rem){:host .button-wrapper{display:flex;justify-self:center;padding:1.5rem 0 0 0;margin:1rem 0 0 0}}@media (min-width: 64rem){:host .button-wrapper{padding:0}}:host .button-wrapper roche-button{width:100%}:host .button-wrapper roche-button:last-child{margin-top:1rem}@media (min-width: 48rem){:host .button-wrapper roche-button{margin-top:0}:host .button-wrapper roche-button:last-child{margin-top:0}:host .button-wrapper roche-button:first-child{margin-right:2rem}}:host roche-select{display:flex;margin-top:1rem}@media (min-width: 48rem){:host roche-select{margin-top:1.5rem}}:host roche-grid{position:fixed;top:0;margin-top:2.5rem;padding:0 1.25rem}@media (min-width: 48rem){:host roche-grid{margin-top:7.5rem}}::slotted(roche-richtext){margin-top:0 !important}:host([open]){opacity:1;pointer-events:auto;transition:0.6s opacity}@media (min-width: 48rem){:host([dir=rtl]) .button-wrapper roche-button:first-child{margin-left:1rem;margin-right:0}}";
const RocheOnClickNoticeStyle0 = rocheOnClickNoticeCss;

const cookieValue = 'accepted';
const RocheOnClickNotice$1 = /*@__PURE__*/ proxyCustomElement(class RocheOnClickNotice extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.disable = false;
        this.hideNoticeCheckbox = false;
        this.open = false;
        this.targetLink = undefined;
        this.downloadLink = undefined;
        this.checkedSelect = false;
        this.isMobile = false;
        this.translations = undefined;
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            event: 'external_link_click',
            component_name: 'roche-on-click-notice',
        };
    }
    // eslint-disable-next-line class-methods-use-this
    get whiteListedDomainsParsed() {
        try {
            const parsed = JSON.parse(DomService.getGlobalConfig().whiteListedDomains);
            return parsed;
        }
        catch (_a) {
            return [];
        }
    }
    handleDownloadClick(event) {
        this.downloadLink = event.detail.url;
        if (this.open || this.disable) {
            this.handleDownload();
            return;
        }
        if (this.shouldPreventNavigation()) {
            event.preventDefault();
            event.stopPropagation();
            this.open = true;
            TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { value: 'open', additionalParameters: {
                    link: `${this.downloadLink}`,
                } }));
            return;
        }
        this.handleDownload();
    }
    handleClick(event) {
        if (this.open || this.disable) {
            return;
        }
        this.targetLink = event.composedPath()
            .filter((element) => element.tagName === 'A');
        this.downloadLink = undefined;
        if (this.targetLink.some((el) => el.href === '')) {
            return;
        }
        if (this.shouldPreventNavigation()) {
            event.preventDefault();
            event.stopPropagation();
            this.open = true;
            TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { value: 'open', additionalParameters: {
                    link: `${this.targetLink}`,
                } }));
        }
    }
    targetLinkHasSkipProperty() {
        return [...this.targetLink].pop().hasAttribute('skipOnClickNotice');
    }
    shouldPreventNavigation() {
        var _a;
        if (this.hideNoticeCheckbox) {
            CookieService.removeCookie('onClickNotice');
        }
        if (CookieService.getCookie('onClickNotice') === cookieValue) {
            return false;
        }
        if (!this.downloadLink && (((_a = this.targetLink) === null || _a === void 0 ? void 0 : _a.length) <= 0 || this.targetLinkHasSkipProperty())) {
            return false;
        }
        const targetLinkHref = this.downloadLink ? this.downloadLink : [...this.targetLink].pop().href;
        if (targetLinkHref.length <= 0 || targetLinkHref.startsWith('blob:')) {
            return false;
        }
        const { baseDomain } = DomService.getGlobalConfig();
        const targetHostname = targetLinkHref ? new URL(targetLinkHref).hostname : '';
        const targetDomain = `${baseDomain}${targetHostname.split(baseDomain)[1]}`;
        const currentHostname = DomService.getLocationHostname();
        const currentDomain = `${baseDomain}${currentHostname.split(baseDomain)[1] || ''}`;
        const shouldRedirect = this.whiteListedDomainsParsed
            .some((domain) => targetDomain.indexOf(domain) > -1 || targetHostname.indexOf(domain) > -1);
        return !(shouldRedirect || targetDomain === currentDomain);
    }
    handleSelectCheck({ detail }) {
        this.checkedSelect = detail.isAnythingSelected;
    }
    handleResize() {
        this.isMobile = window.innerWidth <= breakpoints.medium;
    }
    async componentWillLoad() {
        this.translations = {
            youWillBeRedirected: await TranslationService.translate('please be aware that you are leaving this website.'),
            dontShowMessageAgain: await TranslationService.translate('do not show this message again'),
            continue: await TranslationService.translate('continue'),
            cancel: await TranslationService.translate('cancel'),
        };
        this.handleResize();
    }
    componentDidLoad() {
        this.handleResize();
        DomService.handleTextDirection(this.element);
    }
    render() {
        return (h(Host, { key: 'ac18ce5d903b67b6230d28666bae0a8e9757de49' }, h("roche-grid", { key: 'e603a4214fbd9bfc2c0ae2d1951fbbde1a1ce3e1', layout: '10' }, h("div", { key: '0ed7ac9d6cd1da7fe996bb187e2849fb62c4ef20', class: "wrapper" }, h("div", { key: '68b211259642b3ae5069d87fbbbadf78c5d51c9e' }, h("div", { key: '2b80ae9b5ef5d725a459f6da847ac44c8dbf5ec2', class: 'richtext' }, h("slot", { key: '266bbd4dda1410d160fce43d9f6cbbac804b8100' }, h("roche-headline", { key: '42119592928d25aa271fc242a54ab3200f730c6a', headline: this.translations.youWillBeRedirected, level: this.isMobile ? 6 : 4 }))), !this.hideNoticeCheckbox && h("roche-select", { options: `[{"label":"${this.translations.dontShowMessageAgain}","value":"hide-on-click-notice"}]`, "multi-select": true, labelWrapper: true })), h("div", { key: '639835abcea0d2169981c8431b4ec6cb471fd853', class: "button-wrapper" }, h("roche-button", { key: '1bd2b6caa46b419d292911bac4295bec46ca1432', preventTracking: true, appearance: ButtonAppearance.SECONDARY, text: this.translations.cancel, type: ButtonType.BUTTON, onRocheButtonClick: this.handleCancelClick.bind(this) }), h("roche-button", { key: '63eb277688f6b0a3ed580eb8d6be2e6a2a3be82a', preventTracking: true, text: this.translations.continue, type: ButtonType.BUTTON, onRocheButtonClick: this.handleContinueClick.bind(this) }))))));
    }
    handleCancelClick() {
        this.open = false;
        TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { value: 'cancel', additionalParameters: {
                link: `${this.currentLink}`,
            } }));
    }
    handleContinueClick() {
        var _a;
        TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { value: 'continue', additionalParameters: {
                link: `${this.currentLink}`,
            } }));
        if (CookieService.getConsent().necessary && this.checkedSelect) {
            CookieService.setCookie('onClickNotice', cookieValue, { expires: 182 });
        }
        if (this.downloadLink) {
            this.handleDownload();
            this.open = false;
            return;
        }
        (_a = this.targetLink) === null || _a === void 0 ? void 0 : _a.pop().click();
        this.open = false;
    }
    get currentLink() {
        if (this.downloadLink) {
            return this.downloadLink;
        }
        return this.targetLink ? this.targetLink[this.targetLink.length - 1].href : undefined;
    }
    handleDownload() {
        DownloadService.handleDownload(this.downloadLink);
    }
    get element() { return this; }
    static get style() { return RocheOnClickNoticeStyle0; }
}, [1, "roche-on-click-notice", {
        "disable": [4],
        "hideNoticeCheckbox": [4, "hide-notice-checkbox"],
        "open": [1540],
        "targetLink": [32],
        "downloadLink": [32],
        "checkedSelect": [32],
        "isMobile": [32],
        "translations": [32]
    }, [[8, "triggerDownload", "handleDownloadClick"], [8, "click", "handleClick"], [0, "rocheFormControlChange", "handleSelectCheck"], [9, "resize", "handleResize"]]]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-on-click-notice", "roche-button", "roche-column", "roche-columns", "roche-container", "roche-grid", "roche-headline", "roche-icon", "roche-select", "roche-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-on-click-notice":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheOnClickNotice$1);
            }
            break;
        case "roche-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "roche-column":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "roche-columns":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "roche-container":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "roche-grid":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "roche-headline":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheOnClickNotice = RocheOnClickNotice$1;
const defineCustomElement = defineCustomElement$1;

export { RocheOnClickNotice, defineCustomElement };

//# sourceMappingURL=roche-on-click-notice.js.map